import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Flex } from "../../Flex";
import { Button } from "../../Button";
import { Icon } from "../../Icon";
/**
 * Flattens nested React Fragment components into a single array of child elements.
 *
 * This function recursively traverses the React component tree and:
 * - Processes React Fragment components by recursively flattening their children
 * - Preserves non-Fragment React elements as-is
 * - Filters out invalid elements by returning null
 *
 * @param children - The React children to process, can be any valid ReactNode
 * @returns An array of flattened React elements with Fragments removed, or null for invalid elements
 */
export function flattenFragmentsElement(children) {
    return React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            // Check if it's a Fragment
            if (child.type === React.Fragment) {
                // Recursively handle Fragment children
                return flattenFragmentsElement(child.props.children);
            }
            return child;
        }
        return null;
    });
}
/**
 * FormArrayRow component for handling dynamic form arrays with drag-and-drop functionality
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the sortable row
 * @param {number} props.index - Index of the current row
 * @param {string} props.name - Base name for form field
 * @param {ReactNode} props.children - Child form elements
 * @param {number} props.minLength - Minimum number of rows allowed
 * @param {number} props.maxLength - Maximum number of rows allowed
 * @param {number} props.fieldsLength - Current number of rows
 * @param {Record<string,any>} props.defaultValue - Default values for form fields
 * @param {Function} props.onAdd - Callback when adding a new row
 * @param {Function} props.onRemove - Callback when removing a row
 * @param {boolean} props.enableSorting - Whether drag-and-drop sorting is enabled
 * @param {boolean} props.multiLine - Whether fields should be multiline
 * @param {boolean} props.disabled - Whether the row is disabled
 * @param {boolean} props.readOnly - Whether the row is read-only
 */
export const FormArrayRow = ({ id, index, name, children, minLength, maxLength, fieldsLength, onAdd, onRemove, enableSorting = false, multiLine = true, disabled = false, readOnly = false, }) => {
    // Get sortable functionality from dnd-kit
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    // Define styles for drag and drop visual feedback
    const style = {
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (React.createElement(Flex, { ref: setNodeRef, sx: Object.assign({ alignContent: "flex-start", gap: 2, mb: 1 }, style), "data-testid": "form-array-row" },
        enableSorting && !disabled && !readOnly && (React.createElement(Flex, Object.assign({}, attributes, listeners, { sx: {
                "&:hover": {
                    color: "text.primary",
                },
                alignItems: "center",
                color: "text.secondary",
                cursor: "grab",
                // Ensure the icon is vertically centered with the input fields when multiLine is enabled
                paddingTop: multiLine ? "32px" : 0,
            }, "data-testid": "drag-handle" }),
            React.createElement(Icon, { src: "DotsVertical", size: 16 }))),
        React.createElement(Flex, { sx: { flex: 1, gap: 2 } }, React.Children.map(flattenFragmentsElement(children), (child) => {
            // Clone and enhance child elements with additional props
            if (React.isValidElement(child)) {
                return React.cloneElement(child, Object.assign({}, (child.props && typeof child.props === "object" && "name" in child.props
                    ? Object.assign(Object.assign({}, child.props), {
                        disabled,
                        multiLine,
                        name: `${name}.${index}.${child.props.name}`,
                        readOnly,
                        sx: { paddingBottom: 0 },
                    }) : {})));
            }
            return child;
        })),
        !disabled && !readOnly && (React.createElement(Flex, { sx: {
                alignItems: "center",
                gap: 1,
                justifyContent: "flex-end",
                minWidth: "68px",
                // Ensure the icon is vertically centered with the input fields when multiLine is enabled
                paddingTop: multiLine ? "32px" : 0,
            } },
            fieldsLength > minLength && (React.createElement(Button, { "aria-label": "remove row", variant: "iconWithoutBorder", size: "compact", onClick: () => onRemove(index), 
                // Ensure the removed icons are horizontally aligned for all the rows.
                sx: { marginRight: "auto" } },
                React.createElement(Icon, { size: 16, src: "Trash" }))),
            index === fieldsLength - 1 && fieldsLength < maxLength && (React.createElement(Button, { "aria-label": "add row", variant: "iconWithoutBorder", size: "compact", onClick: onAdd },
                React.createElement(Icon, { size: 16, src: "Add" })))))));
};
